import React, { useEffect, useCallback, useId, useRef } from 'react';
import { X } from 'lucide-react';
import './Modal.css';
import Message from './Message';

/**
 * 自定义 Modal 组件
 * @param {boolean} isOpen - 是否显示
 * @param {string} title - 标题
 * @param {React.ReactNode} children - 内容
 * @param {Array} buttons - 按钮配置 [{label, onClick, variant, disabled}]
 * @param {function} onClose - 关闭回调
 * @param {boolean} closable - 是否显示关闭按钮
 * @param {string} size -尺寸: small, medium, large
 */
const Modal = ({
  isOpen,
  title,
  children,
  buttons = [],
  onClose,
  closable = true,
  size = 'medium',
  className = '',
  initialFocusRef,
  busy = false
}) => {
  const overlayPointerDownRef = useRef(false);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const titleId = useId();
  const canClose = closable && !busy;

  // ESC 键关闭
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && canClose) {
      onClose?.();
    }
  }, [canClose, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusRef.current = document.activeElement;
    const target = initialFocusRef?.current || closeButtonRef.current;
    target?.focus({ preventScroll: true });

    return () => {
      previousFocusRef.current?.focus?.({ preventScroll: true });
    };
  }, [initialFocusRef, isOpen]);

  if (!isOpen) return null;

  const handleOverlayMouseDown = (e) => {
    overlayPointerDownRef.current = e.target === e.currentTarget;
  };

  const handleOverlayClick = (e) => {
    if (overlayPointerDownRef.current && e.target === e.currentTarget && canClose) {
      onClose?.();
    }
    overlayPointerDownRef.current = false;
  };

  const handleDialogKeyDown = (event) => {
    if (event.key !== 'Tab') return;

    const focusable = dialogRef.current?.querySelectorAll(
      'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), '
      + 'select:not(:disabled), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable?.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    }
  };

  const getVariantClass = (variant) => {
    switch (variant) {
      case 'danger': return 'btn-danger';
      case 'success': return 'btn-success';
      case 'warning': return 'btn-warning';
      case 'default': return 'btn-default';
      default: return 'btn-primary';
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={handleOverlayClick}>
      <div
        ref={dialogRef}
        className={`modal-container modal-${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        onKeyDown={handleDialogKeyDown}
      >
        <div className="modal-header">
          <h3 id={titleId} className="modal-title">{title}</h3>
          {canClose && (
            <button
              ref={closeButtonRef}
              type="button"
              className="modal-close"
              onClick={onClose}
              aria-label="关闭"
            >
              <X size={20} aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="modal-body">
          {children}
        </div>
        {buttons.length > 0 && (
          <div className="modal-footer">
            {buttons.map((btn, index) => (
              <button
                key={index}
                type="button"
                className={`btn ${getVariantClass(btn.variant)}`}
                onClick={btn.onClick}
                disabled={busy || btn.disabled}
              >
                {btn.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 确认对话框
 */
export const Confirm = ({
  isOpen,
  title = '确认',
  message,
  confirmText = '确认',
  cancelText = '取消',
  onConfirm,
  onCancel,
  variant = 'danger',
  loading = false,
  disabled = false,
  loadingText = '处理中...'
}) => {
  return (
    <Modal
      isOpen={isOpen}
      title={title}
      closable={!loading}
      onClose={onCancel}
      buttons={[
        {
          label: cancelText,
          onClick: onCancel,
          variant: 'default',
          disabled: loading
        },
        {
          label: loading ? loadingText : confirmText,
          onClick: onConfirm,
          variant: variant,
          disabled: loading || disabled
        }
      ]}
    >
      <div className="confirm-message">{message}</div>
    </Modal>
  );
};

/**
 * 警告对话框
 */
// Compatibility for existing callers. New feedback should use Message directly.
export const Alert = ({ isOpen, message, onClose, type = 'info' }) => (
  <Message message={isOpen ? message : ''} type={type} onClose={onClose} />
);

export default Modal;
